'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard, Blocks, Users, Sparkles, ChevronLeft, ChevronRight,
  BarChart2, Settings, LogOut, ShieldCheck
} from 'lucide-react';

const adminNavGroups = [
  {
    label: 'Overview',
    items: [
      { href: '/admin-panel', label: 'Dashboard', icon: LayoutDashboard, badge: null },
      { href: '/admin-panel#analytics', label: 'Analytics', icon: BarChart2, badge: null },
    ],
  },
  {
    label: 'Manage',
    items: [
      { href: '/admin-panel#blocks', label: 'Blocks', icon: Blocks, badge: '6M' },
      { href: '/admin-panel#users', label: 'Users', icon: Users, badge: '12.4k' },
      { href: '/admin-panel#miracles', label: 'Miracles', icon: Sparkles, badge: '248' },
    ],
  },
  {
    label: 'System',
    items: [
      { href: '/admin-panel#settings', label: 'Settings', icon: Settings, badge: null },
    ],
  },
];

export default function AdminSidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className="flex flex-col h-screen sticky top-0 transition-all duration-300 ease-in-out"
      style={{
        width: collapsed ? '64px' : '240px',
        background: 'rgba(12,12,22,0.98)',
        borderRight: '1px solid rgba(201,168,76,0.15)',
        backdropFilter: 'blur(16px)',
      }}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b border-border ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-display text-sm font-semibold gold-gradient-text tracking-wider whitespace-nowrap overflow-hidden">
            MiracleChain
          </span>
        )}
      </div>
      {/* Admin badge */}
      {!collapsed && (
        <div className="mx-3 mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent/10 border border-accent/20">
          <ShieldCheck size={13} className="text-accent" />
          <span className="text-xs font-medium text-accent">Admin Panel</span>
        </div>
      )}
      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
        {adminNavGroups?.map((group) => (
          <div key={`group-${group?.label}`}>
            {!collapsed && (
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {group?.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group?.items?.map((item) => {
                const isActive = pathname === item?.href || (item?.href?.includes('#') && pathname === '/admin-panel');
                return (
                  <Link
                    key={`admin-nav-${item?.href}`}
                    href={item?.href}
                    title={collapsed ? item?.label : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
                      isActive
                        ? 'sidebar-active' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <item.icon size={17} className="flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item?.label}</span>
                        {item?.badge && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/15 text-primary">
                            {item?.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && item?.badge && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      {/* Bottom */}
      <div className="p-2 border-t border-border space-y-1">
        <button
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all duration-200"
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={17} className="flex-shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
        >
          {collapsed ? <ChevronRight size={15} /> : <><ChevronLeft size={15} /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}